import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import AtaFinalModal from './components/modals/AtaFinalModal';
import { formatDateView, formatTimeView } from './utils/dateUtils';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import usePagination10 from './components/hooks/usePagination10';
import PaginationBar from './components/PaginationBar';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

function AtasFinal() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const [atas, setAtas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ ano: '', data: '', hora: '' });
  const [atualizar, setAtualizar] = useState(false);
  const [selectedAta, setSelectedAta] = useState(null);
  const [generating, setGenerating] = useState(false);
  const totalItems = atas.length;
  const { setPagination } = usePagination10(totalItems);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  useEffect(() => {
    fetchAtas();
  }, [atualizar]);

  const fetchAtas = async () => {
    if (!cnpj) return;
    setLoading(true);
    const ano = localStorage.getItem('ano');
    try {
      const res = await axios.get(`${API_BASE_URL.ATA_FINAL}/all`, {
        params: { cnpjEscola: cnpj, ano: ano ? Number(ano) : undefined },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAtas(res.data || []);
    } catch (err) {
      console.error('Erro ao buscar atas finais', err);
      showDefaultModal('error', 'Erro ao buscar atas finais');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNew = () => {
    setForm({ ano: '', data: '', hora: '' });
    setShowModal(true);
  };

  const handleSave = async (payload) => {
    try {
      const res = await axios.post(`${API_BASE_URL.ATA_FINAL}/save`, { ...payload, cnpjEscola: cnpj }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const saved = (res && res.data) ? res.data : { ...payload, cnpjEscola: cnpj };
      setShowModal(false);
      setSelectedAta(saved);
      setAtualizar(prev => !prev);
    } catch (err) {
      console.error('Erro ao salvar ata final', err);
      showDefaultModal('error', 'Erro ao salvar ata final');
    }
  };

  const handleDelete = (ata) => {
    // mostra modal de confirmação
    showConfirmModal('delete', () => handleConfirmDelete(ata));
  };

  const handleConfirmDelete = async (ata) => {
    try {
      await axios.delete(`${API_BASE_URL.ATA_FINAL}/delete`, {
        params: { ano: ata.ano, cnpjEscola: ata.cnpjEscola, data: ata.data, hora: ata.hora },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAtualizar(prev => !prev);
      setSelectedAta(null);
    } catch (err) {
      console.error('Erro ao excluir ata final', err);
      showDefaultModal('error', 'Erro ao excluir ata final');
    }
  };

  return (
    <Layout>
      <LoadingModal loading={loading} />
      <ResponsiveShadow>
        <PageHeader title="Atas Finais" icon="bi-journal-check" />
        <PageContent>
          <Toolbar>
              <button className="btn btn-success btn-sm botao-igual d-inline-flex align-items-center" onClick={handleOpenNew}>
                <i className="bi bi-plus-lg me-1"></i>Nova Ata Final
              </button>
                <button className="btn btn-primary btn-sm botao-igual d-inline-flex align-items-center" disabled={!selectedAta || generating} onClick={async () => {
                  try {
                    setGenerating(true);
                    const ano = localStorage.getItem('ano');
                    const params = {
                      idEscola: cnpj,
                      ano: Number(ano),
                      data: selectedAta.data,
                      hora: selectedAta.hora,
                    };
                    const res = await axios.post(`${API_BASE_URL.RELATORIO}/gerarAtaAprovacaoPdde`, null, {
                      params,
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'arraybuffer'
                    });
                    const disposition = res.headers['content-disposition'] || res.headers['Content-Disposition'];
                    let filename = `ATA_APROVACAO_PDDE_${ano || ''}.docx`;
                    if (disposition) {
                      const match = disposition.match(/filename="?([^\";]+)"?/);
                      if (match && match[1]) filename = match[1];
                    }
                    const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error('Erro ao gerar ata de aprovação PDDE', err);
                    showDefaultModal('error', 'Erro ao gerar ata de aprovação PDDE');
                  } finally {
                    setGenerating(false);
                  }
                }}>
                  <i className="bi bi-download me-1"></i>Gerar Ata Final
                </button>
              <button className="btn btn-danger btn-sm botao-igual d-inline-flex align-items-center" disabled={!selectedAta} onClick={() => selectedAta && handleDelete(selectedAta)}>
                <i className="bi bi-trash me-1"></i>Excluir
              </button>
          </Toolbar>

          <TableContainer isLoading={false}>
            <DataTable head={
              <tr>
                <th scope="col" style={{ width: '3rem' }}>#</th>
                <th scope="col">Ano</th>
                <th scope="col">Data</th>
                <th scope="col">Hora</th>
              </tr>
            }>
              <tbody>
                {atas.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage).map((a, idx) => (
                  <tr
                    key={`${a.ano}-${a.cnpjEscola}-${a.data}-${a.hora}`}
                    className={selectedAta && selectedAta.ano === a.ano && selectedAta.cnpjEscola === a.cnpjEscola && selectedAta.data === a.data && selectedAta.hora === a.hora ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (selectedAta && selectedAta.ano === a.ano && selectedAta.data === a.data && selectedAta.hora === a.hora) {
                        setSelectedAta(null);
                      } else {
                        setSelectedAta(a);
                      }
                    }}
                  >
                    <td>{setPagination.startIndex + idx + 1}</td>
                    <td>PRESTAÇÃO DE CONTAS {a.ano}</td>
                    <td>{formatDateView(a.data)}</td>
                    <td>{formatTimeView(a.hora)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </TableContainer>
        </PageContent>

        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
      <AtaFinalModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} setForm={setForm} />
    </Layout>
  );
}

export default AtasFinal;
